// Board — the team's kanban. Four columns (New → In Review → In Progress →
// Done); drag a card to a column to move the ticket's status. Built on native
// HTML5 drag-and-drop (no extra dependency). Optimistic so the card jumps
// immediately, then reconciles with the server. RLS lets owner|admin|dev move
// cards. N1: internal only.

import { useMemo, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  STATUS_COLUMNS,
  fetchTickets,
  kindTone,
  severityTone,
  type Ticket,
  type TicketStatus,
} from '@/lib/tickets';

export default function Board() {
  const qc = useQueryClient();
  const ticketsQ = useQuery({ queryKey: ['tickets', 'all'], queryFn: fetchTickets });
  const tickets = ticketsQ.data ?? [];
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<TicketStatus | null>(null);

  const byStatus = useMemo(() => {
    const map: Record<TicketStatus, Ticket[]> = {
      new: [],
      in_review: [],
      in_progress: [],
      done: [],
    };
    for (const t of tickets) map[t.status]?.push(t);
    return map;
  }, [tickets]);

  const move = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TicketStatus }) => {
      const { error } = await supabase.from('tickets').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['tickets', 'all'] });
      const prev = qc.getQueryData<Ticket[]>(['tickets', 'all']);
      qc.setQueryData<Ticket[]>(['tickets', 'all'], (old) =>
        (old ?? []).map((t) => (t.id === id ? { ...t, status } : t)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tickets', 'all'], ctx.prev);
      toast.error('Could not move that ticket.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });

  function onDrop(e: DragEvent<HTMLDivElement>, status: TicketStatus): void {
    e.preventDefault();
    setOverCol(null);
    // Read the dragged id from the drag payload (authoritative), not from React
    // state — state can be cleared by a batched onDragEnd before this runs.
    const id = e.dataTransfer.getData('text/plain') || dragId;
    setDragId(null);
    if (!id) return;
    const t = tickets.find((x) => x.id === id);
    if (!t || t.status === status) return;
    move.mutate({ id, status });
  }

  return (
    <>
      {ticketsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading board…</p>
      ) : ticketsQ.isError ? (
        <p className="text-sm text-alert">Couldn't load the board. Refresh to try again.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATUS_COLUMNS.map((col) => {
            const items = byStatus[col.value];
            const isOver = overCol === col.value;
            return (
              <div
                key={col.value}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (overCol !== col.value) setOverCol(col.value);
                }}
                onDragLeave={() => setOverCol((c) => (c === col.value ? null : c))}
                onDrop={(e) => onDrop(e, col.value)}
                className={`rounded-xl p-3 transition-colors min-h-[140px] neo-inset ${
                  isOver ? 'bg-forest-soft/60' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-sm font-semibold text-foreground">{col.label}</span>
                  <span className="text-xs text-muted-foreground neo-chip px-1.5 py-0.5 rounded-full">
                    {items.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {items.map((t) => (
                    <BoardCard
                      key={t.id}
                      ticket={t}
                      dragging={dragId === t.id}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', t.id);
                        e.dataTransfer.effectAllowed = 'move';
                        setDragId(t.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverCol(null);
                      }}
                    />
                  ))}
                  {items.length === 0 && (
                    <p className="text-xs text-muted-foreground/70 px-1 py-3 text-center">
                      Drop tickets here
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function BoardCard({
  ticket,
  dragging,
  onDragStart,
  onDragEnd,
}: {
  ticket: Ticket;
  dragging: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}) {
  const kt = kindTone(ticket.kind);
  const st = severityTone(ticket.severity);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`rounded-lg neo-raised p-3 cursor-grab active:cursor-grabbing transition-opacity ${
        dragging ? 'opacity-40' : 'opacity-100'
      }`}
    >
      <p className="text-sm font-medium text-foreground leading-snug">{ticket.title}</p>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${kt.bg} ${kt.text}`}>{kt.label}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span>
      </div>
      {ticket.feature_area && (
        <p className="text-[11px] text-muted-foreground mt-2 truncate">{ticket.feature_area}</p>
      )}
      <p className="text-[10px] text-muted-foreground/70 mt-1">
        {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
      </p>
    </div>
  );
}
